<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import store from "@/store";

import type { ReportType, ReportPayloadType } from "@/common/types/report";

import { toast } from "@/common/utils/toast";
import {
  reportList,
  reportCreate,
  reportDelete,
} from "@/common/api/rest/report/report";

import {
  baseConfirm,
  baseInput,
  baseModal,
  cardReport,
  svgIcon,
} from "@/common/components";

import { reportForm } from "@/components";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    evaluationId: string;
    width?: string;
    showTitle?: boolean;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    showTitle: true,
    isEdit: true,
  },
);

const DEFAULT_PAYLOAD = {
  name: "",
  description: null,
};

const searchInput = ref("");
const reports = ref<ReportType[]>([]);
const selected = ref<ReportType | null>(null);
const payload = ref<ReportPayloadType>({ ...DEFAULT_PAYLOAD });

const showModalAdd = ref(false);
const showConfirmDel = ref(false);

const accountId = computed(() => route.params.id as string);

function fetchData() {
  reportList(accountId.value, {
    evaluation_id: props.evaluationId,
    full: true,
    search: searchInput.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        reports.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update() {
  emit("update-data");
}

function add() {
  payload.value = { ...DEFAULT_PAYLOAD };
  showModalAdd.value = true;
}

function addCancel() {
  showModalAdd.value = false;
  payload.value = { ...DEFAULT_PAYLOAD };
}

function addConfirm() {
  showModalAdd.value = false;

  reportCreate(accountId.value, {
    evaluation_id: props.evaluationId,
    name: payload.value.name,
    description: payload.value.description,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        fetchData();
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function remove(data: ReportType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);

  reportDelete(accountId.value, selected.value.report_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeCancel();
        update();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function selectReport(data: ReportType) {
  router.push(`/${accountId.value}/report/${data.report_id}`);
}

function copyReport(data: ReportType) {
  console.log("copy-report", data);
}

function cleanSearch() {
  if (!searchInput.value) {
    fetchData();
  }
}

function searchValue() {
  fetchData();
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <template v-if="showTitle">
      <div class="title">
        {{ t("common.labels.reports") }}
      </div>
    </template>

    <section class="header">
      <div class="search">
        <base-input
          v-model="searchInput"
          background="#fff"
          color="#000"
          :placeholder="t('common.labels.search')"
          @update:model-value="cleanSearch()"
          @key:enter="searchValue()"
        >
          <template #sufix>
            <div class="icon">
              <svg-icon icon="search" size="1.75rem" @click="searchValue()" />
            </div>
          </template>
        </base-input>
      </div>
      <div class="actions">
        <button class="btn btn-md btn-primary" @click="add()">
          <svg-icon icon="add" size="1.1rem" />
          <span>
            {{ t("common.labels.report")?.toUpperCase() }}
          </span>
        </button>
      </div>
    </section>

    <section class="reports">
      <template v-for="item in reports" :key="item.report_id">
        <card-report
          :data="item"
          @copy="copyReport(item)"
          @edit="selectReport(item)"
          @delete="remove(item)"
        />
      </template>

      <template v-if="reports.length === 0">
        <div class="empty">
          {{ t("evaluation.texts.empty_evaluation") }}
        </div>
      </template>
    </section>

    <base-confirm
      :show="showConfirmDel"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.report'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: selected?.name,
        })
      "
      @cancel="removeCancel"
      @confirm="removeConfirm"
    />

    <base-modal
      :show="showModalAdd"
      :show-button-confirm="true"
      :button-confirm="t('common.labels.confirm')"
      :button-close="t('common.labels.cancel')"
      @close="addCancel()"
      @confirm="addConfirm()"
    >
      <template #content>
        <report-form
          :title="t('common.labels.add', { concat: t('common.labels.report') })"
          :data="payload"
          @update-data="payload = $event"
        />
      </template>
    </base-modal>
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.5rem;
  }

  .header {
    display: grid;
    grid-template-columns: 2fr 1fr;
    justify-content: space-between;
    gap: 1rem;
    height: 2.9rem;

    .search {
      width: 100%;

      .icon {
        cursor: pointer;
        color: var(--text-off);

        &:hover {
          color: var(--primary);
        }
      }
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
    }
  }

  .reports {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: calc(100dvh - 2.9rem - 1rem - 6rem);
    overflow: auto;

    &.scrollable {
      padding-inline-end: 0.5rem;
    }

    .list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding-block-end: 2rem;
    }

    .empty {
      padding-inline: 0.5rem;
    }
  }
}
</style>
